import { Box, BoxProps, useId, useTheme } from "@chakra-ui/react";
import { css, Global } from "@emotion/react";
//@ts-ignore
import Mazemap from "@itpi/mazemap";
import { MarkerOptions } from "mapbox-gl";
import { useContext, useEffect } from "react";

import { MapContext } from "../context/map.context";

import "@itpi/mazemap/mazemap.min.css";

export type MapPin = MarkerOptions & {
  lat: number;
  lng: number;
  zLevel?: number;
  onClick?: () => void;
  glyph?: string;
  shape?: string;
  size?: number;
  // Optional variant to render a MazeMarker (default) or a BlueDot with accuracy radius
  variant?: "marker" | "blueDot";
  // Accuracy radius in meters for BlueDot variant
  accuracy?: number;
};

export const defaultLocation = {
  pitch: 0,
  bearing: 0,
  zoom: 14.5,
  center: {
    lng: -2.785908,
    lat: 54.008355,
  },
};

const Map = (boxProps: BoxProps) => {
  const mapId = useId();
  const { map, setMap, pins } = useContext(MapContext);
  const theme = useTheme();

  // @ts-ignore
  window.Mazemap = Mazemap;

  useEffect(() => {
    const mazemap = new Mazemap.Map({
      container: mapId,
      campuses: 341,
      zLevelControl: false,
      ...defaultLocation,
    });

    mazemap.on("load", () => {
      const floorBar = new Mazemap.ZLevelBarControl();
      mazemap.addControl(floorBar, "top-left");
      
      mazemap.on("styleimagemissing", () => {
        // Do nothing - suppress the errors about missing images
      });
    });

    setMap(mazemap);
  }, [mapId, setMap]);

  useEffect(() => {
    if (map && pins) {
      const disposables: { remove: () => void }[] = [];

      pins.forEach((pin) => {
        const { lat, lng, onClick, variant, accuracy, ...config } = pin as any;

        if (variant === "blueDot") {
          // Render a BlueDot at the specified location with an accuracy radius.
          const dot = new Mazemap.BlueDot({ map });
          dot.setLngLatAnimated({ lng, lat });
          if (typeof accuracy === "number") {
            dot.setAccuracy(accuracy);
          }
          dot.show();

          // Keep click interactions by overlaying an invisible marker at the same spot.
          let clickMarker: any | undefined;
          if (onClick) {
            clickMarker = new Mazemap.MazeMarker({
              color: "rgba(0,0,0,0)",
              shape: "circle",
              size: config.size || 24,
            })
              .setLngLat({ lat, lng })
              .addTo(map);
            clickMarker.on("click", onClick);
          }

          disposables.push({
            remove: () => {
              if (clickMarker) clickMarker.remove();
              if (dot && typeof dot.remove === "function") {
                dot.remove();
              } else if (dot && typeof dot.hide === "function") {
                dot.hide();
              }
            },
          });
        } else {
          // Default to a standard MazeMarker pin
          const marker = new Mazemap.MazeMarker({
            color: theme.colors?.["lancsRed"]?.["300"],
            ...config,
          })
            .setLngLat({ lat, lng })
            .addTo(map);
          if (onClick) marker.on("click", onClick);
          disposables.push({ remove: () => marker.remove() });
        }
      });

      return () => disposables.forEach((d) => d.remove());
    }
  }, [pins, map, theme.colors]);

  return (
    <>
      <Global
        styles={css`
          .mapboxgl-ctrl-bottom-left {
            z-index: 100;
          }

          .mazemap-control-zlevel-bar {
            color: black;
          }
        `}
      />
      <Box id={mapId} w="full" h="full" {...boxProps} />
    </>
  );
};

export default Map;
